// MAP PICKUPS render (owner-approved 2026-11-02): relic caches, elite / boss chests and the four field items, in the
// "Resonance Relics v2" language of pickups.ts (flat-faceted cel bodies with vertex colour + emissive, inverted-hull ink,
// one instanced mesh per type, terrain-following ground marks, thin locator beacons, pooled VFX). Render-only: reads
// sim.loot and the loot SimEvents, never writes the sim. Every bright flash goes through FLASH (src/render/a11y.ts):
// stars / lights / flash spheres via the Fx primitives (already gated), pulse and blink rates via FLASH.rate.
//
//  - Relic cache "Reliquary": a dark violet stone plinth carrying a jade casket with gold bands and a faceted gold-and-
//    violet crown lid. A tall faint violet-gold column marks it across the big map. Opening (CACHE_REVEAL s): the lid lifts
//    and turns, a pale crystal heart rises out of it inside a bright column; then the lid settles beside the dimmed casket.
//  - Chests: a slate-teal strongbox with silver posts and a teal lock gem (elite) / a larger crimson-violet one with gold
//    trim and a white-gold gem (boss). Drop: falls in with a bounce. Open: the lid flies up turning, a light shaft and
//    rising motes, then the box sinks away.
//  - Field items: Mend (jade leaf-bud around a pearl core), Lodestar (cobalt octahedron with silver poles and an orbiting
//    cyan band), Sunburst (eight gold rays round a white-gold core), Fervor (twin crimson-orange flame fangs on an ember
//    base). Each bobs and turns over its own coloured ground ring; it blinks for the last FIELD_BLINK s.
import * as THREE from 'three/webgpu';
import { attribute, color } from 'three/tsl';
import type { Sim, SimEvent } from '../core/sim';
import { CACHE_REVEAL, FIELD_BLINK, FIELD_LIFE, type FieldKind } from '../core/pickupsField';
import { AtlasQuads, crystal, Facets, hullGeometry, pickupPosition, ring, type Paint, type V3 } from './pickups';
import { RenderStyle, toonMaterial } from './toon';
import { FLASH } from './a11y';
import type { Fx } from './fx';

const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _q = new THREE.Quaternion(), _q2 = new THREE.Quaternion();
const _z = new THREE.Vector3(0, 0, 1), _a = new THREE.Vector3();
const TAU = Math.PI * 2;
const ease = (u: number) => 1 - (1 - u) * (1 - u);

// ---------------------------------------------------------------- shapes ----------------------------------------------------------------
interface Look { geo: THREE.BufferGeometry; ink: number; emisK: number; tris: number }

/** Reliquary base: octagonal plinth + step + hex jade casket with gold bands (sits on y = 0, top at 0.78) */
function cacheBase(): Look {
  const STONE = 0x3a3550, STONE_HI = 0x6a6290, JADE = 0x2fb7a8, JADE_D = 0x1b6f6e, GOLD = 0xf3c95b, GOLD_D = 0xa8803a;
  const f = new Facets();
  const p0 = ring(8, 0.66, 0, 1, 1, 0, 0, TAU / 16), p1 = ring(8, 0.58, 0.18, 1, 1, 0, 0, TAU / 16);
  f.loft(p0, p1, (k) => (k % 2 ? [STONE, 0] : [STONE_HI, 0.02]), [0, 0.09, 0]); f.cap(p1, [STONE_HI, 0.03], [0, 0, 0]);
  const s0 = ring(8, 0.48, 0.18, 1, 1), s1 = ring(8, 0.44, 0.3, 1, 1);
  f.loft(s0, s1, (k) => (k % 2 ? [STONE_HI, 0.03] : [STONE, 0]), [0, 0.24, 0]); f.cap(s1, [STONE, 0], [0, 0.2, 0]);
  // casket: hex prism with two gold bands
  const band = (y0: number, y1: number) => {
    const b0 = ring(6, 0.37, y0, 1, 1, 0, 0, TAU / 12), b1 = ring(6, 0.37, y1, 1, 1, 0, 0, TAU / 12);
    f.loft(b0, b1, (k) => (k % 2 ? [GOLD, 0.35] : [GOLD_D, 0.1]), [0, (y0 + y1) / 2, 0]);
  };
  const c0 = ring(6, 0.34, 0.34, 1, 1, 0, 0, TAU / 12), c1 = ring(6, 0.34, 0.74, 1, 1, 0, 0, TAU / 12);
  f.loft(c0, c1, (k) => (k % 2 ? [JADE, 0.3] : [JADE_D, 0.1]), [0, 0.54, 0]);
  band(0.3, 0.36); band(0.72, 0.8);
  f.cap(ring(6, 0.37, 0.8, 1, 1, 0, 0, TAU / 12), [GOLD_D, 0.05], [0, 0, 0]);
  // a violet rune gem on every other face
  for (let k = 0; k < 6; k += 2) {
    const a = (k / 6) * TAU + TAU / 12 + TAU / 12, cx = Math.cos(a) * 0.35, cz = Math.sin(a) * 0.35;
    f.box([cx, 0.55, cz], [0.07, 0.09, 0.03], -a + Math.PI / 2, (fc) => (fc === 4 ? [0xc8a8ff, 0.9] : [0x8a5ad8, 0.8]));
  }
  return { geo: f.build(), ink: 0x1e1a30, emisK: 0.75, tris: f.tris };
}
/** Reliquary lid: gold hex frustum, four gold fins, violet crown gem (origin at its base) */
function cacheLid(): Look {
  const GOLD = 0xf3c95b, GOLD_D = 0xa8803a, VIO = 0x8a5ad8, VIO_HI = 0xd8c0ff;
  const f = new Facets();
  const l0 = ring(6, 0.4, 0, 1, 1, 0, 0, TAU / 12), l1 = ring(6, 0.16, 0.24, 1, 1, 0, 0, TAU / 12);
  f.loft(l0, l1, (k) => (k % 2 ? [GOLD, 0.3] : [GOLD_D, 0.1]), [0, 0.1, 0]); f.cap(l0, [GOLD_D, 0], [0, 0.2, 0]);
  crystal(f, 6, [0, 0.22, 0], [0, 0.56, 0], 0.15, 0.13, 0.4, 1, (k, up) => (up ? (k % 2 ? [VIO_HI, 0.95] : [VIO, 0.8]) : [VIO, 0.6]));
  for (let k = 0; k < 4; k++) {
    const a = (k / 4) * TAU + TAU / 8;
    f.fan([[Math.cos(a) * 0.3, 0.06, Math.sin(a) * 0.3], [Math.cos(a + 0.18) * 0.18, 0.2, Math.sin(a + 0.18) * 0.18], [Math.cos(a - 0.18) * 0.18, 0.2, Math.sin(a - 0.18) * 0.18]],
      [Math.cos(a) * 0.36, 0.38, Math.sin(a) * 0.36], (j) => (j === 1 ? [GOLD, 0.45] : [GOLD_D, 0.15]), [0, 0.2, 0]);
  }
  return { geo: f.build(), ink: 0x3a2a14, emisK: 0.75, tris: f.tris };
}
/** the crystal heart that rises out of an opening cache (centred) */
function cacheCore(): Look {
  const f = new Facets();
  crystal(f, 6, [0, -0.28, 0], [0, 0.34, 0], 0.05, 0.17, 0.35, 1, (k, up) => (up ? (k % 2 ? [0xffffff, 1] : [0xe0ccff, 0.95]) : (k % 2 ? [0xc8a8ff, 0.9] : [0x9a74e8, 0.8])));
  return { geo: f.build(), ink: 0x3a2a60, emisK: 1.1, tris: f.tris };
}

interface ChestPal { body: number; bodyD: number; face: number; trim: number; trimD: number; gem: number; gemHi: number; lid: number; lidD: number; ink: number }
const ELITE_PAL: ChestPal = { body: 0x2a5a6c, bodyD: 0x1a3a48, face: 0x3a7488, trim: 0xd6e2ea, trimD: 0x8a9aa6, gem: 0x37d8ce, gemHi: 0xb8fff4, lid: 0x346a7e, lidD: 0x214656, ink: 0x10222c };
const BOSS_PAL: ChestPal = { body: 0x6a1c44, bodyD: 0x40102a, face: 0x8a2858, trim: 0xf3c95b, trimD: 0xa8803a, gem: 0xfff0b0, gemHi: 0xffffff, lid: 0x7a2050, lidD: 0x4a1230, ink: 0x2a0816 };
/** strongbox body: box with corner posts, a mid band and a lock plate + gem on the front (+Z); sits on y = 0, top at 0.56 */
function chestBody(P: ChestPal): Look {
  const f = new Facets();
  f.box([0, 0.28, 0], [0.48, 0.27, 0.31], 0, (fc) => (fc === 4 ? [P.bodyD, 0] : fc === 0 || fc === 2 ? [P.face, 0.05] : [P.body, 0.03]));
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) f.box([sx * 0.46, 0.28, sz * 0.3], [0.055, 0.29, 0.055], 0, (fc) => (fc === 4 ? [P.trim, 0.3] : fc % 2 ? [P.trim, 0.2] : [P.trimD, 0.05]));
  f.box([0, 0.06, 0], [0.5, 0.05, 0.33], 0, (fc) => (fc === 4 ? [P.trim, 0.25] : [P.trimD, 0.05]));
  f.box([0, 0.36, 0.31], [0.11, 0.13, 0.03], 0, (fc) => (fc === 4 ? [P.trim, 0.35] : [P.trimD, 0.1]));
  crystal(f, 4, [0, 0.29, 0.345], [0, 0.45, 0.345], 0.06, 0.07, 0.5, 0.6, (k, up) => (up ? [P.gemHi, 1] : k % 2 ? [P.gem, 0.9] : [P.gemHi, 0.85]), Math.PI / 4);
  return { geo: f.build(), ink: P.ink, emisK: 0.7, tris: f.tris };
}
/** arched lid (origin at its base centre): a 6-point arch extruded along X, two trim arches */
function chestLid(P: ChestPal): Look {
  const f = new Facets();
  const arch = (x: number, k: number): V3[] => [[x, 0, -0.33 * k], [x, 0.13 * k, -0.27 * k], [x, 0.21 * k, -0.12 * k], [x, 0.21 * k, 0.12 * k], [x, 0.13 * k, 0.27 * k], [x, 0, 0.33 * k]];
  const strip = (x0: number, x1: number, k: number, top: Paint, side: Paint) => {
    const a = arch(x0, k), b = arch(x1, k), c: V3 = [(x0 + x1) / 2, 0.02, 0];
    for (let i = 0; i < a.length - 1; i++) { const p = i === 2 ? top : side; f.tri(a[i], a[i + 1], b[i + 1], p, c); f.tri(a[i], b[i + 1], b[i], p, c); }
    f.tri(a[0], a[2], a[1], side, c); f.tri(a[0], a[3], a[2], side, c); f.tri(a[0], a[5], a[3], side, c); f.tri(a[3], a[5], a[4], side, c);
    f.tri(b[0], b[2], b[1], side, c); f.tri(b[0], b[3], b[2], side, c); f.tri(b[0], b[5], b[3], side, c); f.tri(b[3], b[5], b[4], side, c);
    f.tri(a[0], b[0], b[5], side, c); f.tri(a[0], b[5], a[5], side, c);
  };
  strip(-0.5, 0.5, 1, [P.lid, 0.08], [P.lidD, 0.02]);
  for (const x of [-0.36, 0.36]) strip(x - 0.045, x + 0.045, 1.08, [P.trim, 0.35], [P.trimD, 0.1]);
  strip(-0.53, 0.53, 0.25, [P.trim, 0.3], [P.trimD, 0.08]); // rim along the hinge line
  return { geo: f.build(), ink: P.ink, emisK: 0.7, tris: f.tris };
}

/** Mend: three jade leaf crystals fanning out of a pearl-white core */
function mendLook(): Look {
  const LEAF = 0x26a854, LEAF_HI = 0x5fd88a, LEAF_D = 0x146a38, PEARL = 0xfff6e0;
  const f = new Facets();
  for (let k = 0; k < 3; k++) {
    const a = (k / 3) * TAU, cx = Math.cos(a), cz = Math.sin(a);
    crystal(f, 4, [cx * 0.06, -0.12, cz * 0.06], [cx * 0.3, 0.3, cz * 0.3], 0.04, 0.1, 0.45, 0.45, (j, up) => (up ? (j % 2 ? [LEAF_HI, 0.6] : [LEAF, 0.4]) : (j % 2 ? [LEAF, 0.3] : [LEAF_D, 0.1])), a);
  }
  const c = ring(6, 0.12, 0.02, 1, 1);
  f.fan(c, [0, 0.2, 0], (j) => (j % 2 ? [PEARL, 0.9] : [0xffe0c0, 0.8]), [0, 0.04, 0]);
  f.fan(c, [0, -0.16, 0], () => [0xe8d8b8, 0.4], [0, 0, 0]);
  return { geo: f.build(), ink: 0x0e3a20, emisK: 0.5, tris: f.tris };
}
/** Lodestar: cobalt octahedron, silver pole spikes, an orbiting cyan band (spin) */
function vacuumLook(): Look {
  const CO = 0x2f6bff, CO_HI = 0x9cc0ff, CO_D = 0x1a3a90, SIL = 0xe0e8f0, CY = 0x5ff0ff;
  const f = new Facets(), C: V3 = [0, 0, 0];
  const mid = ring(4, 0.2, 0, 1, 1);
  f.fan(mid, [0, 0.24, 0], (k) => (k % 2 ? [CO_HI, 0.6] : [CO, 0.4]), C);
  f.fan(mid, [0, -0.24, 0], (k) => (k % 2 ? [CO, 0.3] : [CO_D, 0.1]), C);
  crystal(f, 4, [0, 0.2, 0], [0, 0.42, 0], 0.05, 0.03, 0.5, 1, (k) => (k % 2 ? [SIL, 0.5] : [0x9aa6b4, 0.2]), Math.PI / 4);
  crystal(f, 4, [0, -0.2, 0], [0, -0.38, 0], 0.05, 0.03, 0.5, 1, (k) => (k % 2 ? [SIL, 0.4] : [0x9aa6b4, 0.15]), Math.PI / 4);
  for (let k = 0; k < 8; k++) { // orbiting band of eight beads, tilted 20°
    const a = (k / 8) * TAU, r = 0.33, y = Math.sin(a) * 0.1;
    f.box([Math.cos(a) * r, y, Math.sin(a) * r], [0.035, 0.035, 0.06], -a, (fc) => (fc === 4 ? [0xd8ffff, 0.95] : [CY, 0.8]), -1.6);
  }
  return { geo: f.build(), ink: 0x10204a, emisK: 0.8, tris: f.tris };
}
/** Sunburst: eight flat gold rays (long / short alternating) round a white-gold core */
function clearLook(): Look {
  const G = 0xffc93a, G_HI = 0xfff2b0, G_D = 0xc08a1a, CORE = 0xffffff;
  const f = new Facets();
  for (let k = 0; k < 8; k++) {
    const a = (k / 8) * TAU, len = k % 2 ? 0.3 : 0.44, cx = Math.cos(a), cz = Math.sin(a), px = -cz, pz = cx;
    const b: V3 = [cx * 0.12, 0, cz * 0.12], t: V3 = [cx * len, 0.02, cz * len], w = 0.07;
    const L: V3 = [b[0] + px * w, 0, b[2] + pz * w], R: V3 = [b[0] - px * w, 0, b[2] - pz * w];
    const top: V3 = [cx * 0.2, 0.07, cz * 0.2], bot: V3 = [cx * 0.2, -0.05, cz * 0.2], ac: V3 = [cx * 0.2, 0, cz * 0.2];
    f.tri(L, t, top, [G_HI, 0.75], [ac[0], -1, ac[2]]); f.tri(t, R, top, [G, 0.55], [ac[0], -1, ac[2]]);
    f.tri(L, top, R, [G, 0.5], [ac[0] * 2, -1, ac[2] * 2]);
    f.tri(L, t, bot, [G_D, 0.2], [ac[0], 1, ac[2]]); f.tri(t, R, bot, [G_D, 0.2], [ac[0], 1, ac[2]]);
  }
  const c = ring(8, 0.15, 0, 1, 1);
  f.fan(c, [0, 0.14, 0], (k) => (k % 2 ? [CORE, 1] : [G_HI, 0.95]), [0, 0, 0]);
  f.fan(c, [0, -0.1, 0], () => [G_D, 0.3], [0, 0, 0]);
  return { geo: f.build(), ink: 0x4a2a06, emisK: 0.85, tris: f.tris };
}
/** Fervor: two curved crimson-orange flame fangs over an ember-dark base */
function furyLook(): Look {
  const RED = 0xff4a2a, OR = 0xffb050, DARK = 0x7a1a14, EMB = 0x3a1210;
  const f = new Facets();
  const b0 = ring(6, 0.17, -0.22, 1, 0.8), b1 = ring(6, 0.13, -0.1, 1, 0.8);
  f.loft(b0, b1, (k) => (k % 2 ? [EMB, 0.1] : [DARK, 0.25]), [0, -0.16, 0]); f.cap(b0, [EMB, 0], [0, -0.16, 0]); f.cap(b1, [DARK, 0.3], [0, -0.1, 0]);
  const fang = (s: number, h: number) => {
    // two stacked crystals bend the fang outward then up (flame tongue)
    crystal(f, 4, [s * 0.05, -0.1, 0], [s * 0.16, 0.12, 0.02], 0.065, 0.07, 0.5, 0.7, (k, up) => (up ? [OR, 0.7] : k % 2 ? [RED, 0.5] : [DARK, 0.2]), Math.PI / 4);
    crystal(f, 4, [s * 0.16, 0.1, 0.02], [s * 0.08, h, -0.02], 0.06, 0.05, 0.4, 0.7, (k, up) => (up ? (k % 2 ? [0xffe0a0, 0.95] : [OR, 0.8]) : [RED, 0.55]), Math.PI / 4);
  };
  fang(-1, 0.5); fang(1, 0.4);
  return { geo: f.build(), ink: 0x2a0806, emisK: 0.85, tris: f.tris };
}

// ---------------------------------------------------------------- atlas ----------------------------------------------------------------
/** 4×2 atlas, 256 px cells: 0 beacon taper · 1 soft ring · 2 sunburst rays · 3 soft dot · 4 reliquary rune ring · 5 chest mark · 6 inked ring (normal blend, tinted) */
const CELL = { beacon: 0, ring: 1, rays: 2, dot: 3, rune: 4, chest: 5, inkRing: 6 } as const;
let atlasTex: THREE.Texture | null = null;
function atlas(): THREE.Texture {
  if (atlasTex) return atlasTex;
  const S = 256, c = document.createElement('canvas'); c.width = S * 4; c.height = S * 2; const g = c.getContext('2d')!;
  const at = (cell: number, fn: () => void) => { g.save(); g.translate((cell % 4) * S + S / 2, Math.floor(cell / 4) * S + S / 2); fn(); g.restore(); };
  const R = S * 0.46;
  at(CELL.beacon, () => {
    const grd = g.createLinearGradient(0, R, 0, -R); grd.addColorStop(0, 'rgba(255,255,255,1)'); grd.addColorStop(0.5, 'rgba(255,255,255,0.5)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd; g.beginPath(); g.moveTo(-S * 0.2, R); g.lineTo(-S * 0.03, -R); g.lineTo(S * 0.03, -R); g.lineTo(S * 0.2, R); g.closePath(); g.fill();
  });
  at(CELL.ring, () => {
    const grd = g.createRadialGradient(0, 0, R * 0.62, 0, 0, R); grd.addColorStop(0, 'rgba(255,255,255,0)'); grd.addColorStop(0.55, 'rgba(255,255,255,1)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd; g.beginPath(); g.arc(0, 0, R, 0, TAU); g.fill();
  });
  at(CELL.rays, () => {
    for (let k = 0; k < 16; k++) {
      g.save(); g.rotate((k / 16) * TAU); const L = k % 2 ? R * 0.75 : R;
      const grd = g.createLinearGradient(0, 0, L, 0); grd.addColorStop(0, 'rgba(255,255,255,0.0)'); grd.addColorStop(0.35, 'rgba(255,255,255,0.9)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = grd; g.beginPath(); g.moveTo(R * 0.12, -S * 0.03); g.lineTo(L, 0); g.lineTo(R * 0.12, S * 0.03); g.closePath(); g.fill(); g.restore();
    }
    const grd = g.createRadialGradient(0, 0, R * 0.8, 0, 0, R); grd.addColorStop(0, 'rgba(255,255,255,0)'); grd.addColorStop(0.6, 'rgba(255,255,255,0.8)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd; g.beginPath(); g.arc(0, 0, R, 0, TAU); g.fill();
  });
  at(CELL.dot, () => {
    const grd = g.createRadialGradient(0, 0, 0, 0, 0, R); grd.addColorStop(0, 'rgba(255,255,255,1)'); grd.addColorStop(0.4, 'rgba(255,255,255,0.5)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd; g.beginPath(); g.arc(0, 0, R, 0, TAU); g.fill();
  });
  at(CELL.rune, () => { // gold rune ring: inked circle broken by six diamond studs
    g.lineCap = 'round';
    for (const [w, col] of [[30, 'rgba(30,20,48,0.9)'], [14, '#f3c95b']] as [number, string][]) {
      g.strokeStyle = col; g.lineWidth = w;
      for (let k = 0; k < 6; k++) { g.beginPath(); g.arc(0, 0, R * 0.8, (k / 6) * TAU + 0.2, ((k + 1) / 6) * TAU - 0.2); g.stroke(); }
    }
    for (let k = 0; k < 6; k++) {
      const a = (k / 6) * TAU, x = Math.cos(a) * R * 0.8, y = Math.sin(a) * R * 0.8, d = 16;
      g.fillStyle = 'rgba(30,20,48,0.9)'; g.beginPath(); g.moveTo(x, y - d - 6); g.lineTo(x + d + 6, y); g.lineTo(x, y + d + 6); g.lineTo(x - d - 6, y); g.closePath(); g.fill();
      g.fillStyle = '#c8a8ff'; g.beginPath(); g.moveTo(x, y - d); g.lineTo(x + d, y); g.lineTo(x, y + d); g.lineTo(x - d, y); g.closePath(); g.fill();
    }
  });
  at(CELL.chest, () => { // four inward corner brackets (white, tinted per chest)
    g.lineCap = 'round'; g.lineJoin = 'round';
    for (const [w, col] of [[34, 'rgba(20,16,28,0.85)'], [16, '#ffffff']] as [number, string][]) {
      g.strokeStyle = col; g.lineWidth = w;
      for (let k = 0; k < 4; k++) { g.save(); g.rotate((k / 4) * TAU + Math.PI / 4); g.beginPath(); g.moveTo(R * 0.55, -R * 0.28); g.lineTo(R * 0.82, 0); g.lineTo(R * 0.55, R * 0.28); g.stroke(); g.restore(); }
    }
  });
  at(CELL.inkRing, () => { // white band with a dark ink edge either side, so a tinted ring keeps its colour on bright grass
    g.lineWidth = 34; g.strokeStyle = 'rgba(16,14,24,0.8)'; g.beginPath(); g.arc(0, 0, R * 0.78, 0, TAU); g.stroke();
    g.lineWidth = 20; g.strokeStyle = '#ffffff'; g.beginPath(); g.arc(0, 0, R * 0.78, 0, TAU); g.stroke();
  });
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
  return (atlasTex = t);
}

// ---------------------------------------------------------------- instanced bodies ----------------------------------------------------------------
class Bodies {
  readonly mesh: THREE.InstancedMesh; readonly hull: THREE.InstancedMesh | null = null;
  private inst: THREE.InstancedBufferAttribute; n = 0;
  constructor(scene: THREE.Scene, style: RenderStyle, L: Look, readonly cap: number, scale: number, extrude: number) {
    L.geo.scale(scale, scale, scale);
    const mat = style === 'cel' ? toonMaterial() : new THREE.MeshStandardNodeMaterial({ roughness: 0.45, metalness: 0.2, flatShading: true });
    mat.vertexColors = true;
    this.inst = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3); this.inst.setUsage(THREE.DynamicDrawUsage);
    L.geo.setAttribute('inst', this.inst);
    mat.positionNode = pickupPosition(0, 0);
    // instanceColor carries the brightness (opening / spent / blink); emissive rides on it so a dimmed body also glows less
    mat.emissiveNode = attribute('color', 'vec3').mul(attribute('emis', 'float')).mul(L.emisK);
    this.mesh = new THREE.InstancedMesh(L.geo, mat, cap);
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3).fill(1), 3);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.visible = false; this.mesh.castShadow = false;
    scene.add(this.mesh);
    if (style === 'cel') {
      const om = new THREE.MeshBasicNodeMaterial({ side: THREE.BackSide });
      om.colorNode = color(L.ink); om.positionNode = pickupPosition(extrude, 0);
      const hg = hullGeometry(L.geo); hg.setAttribute('inst', this.inst);
      const h = new THREE.InstancedMesh(hg, om, cap);
      h.instanceMatrix = this.mesh.instanceMatrix; h.frustumCulled = false; h.count = 0; h.visible = false;
      scene.add(h); (this as { hull: THREE.InstancedMesh | null }).hull = h;
    }
  }
  /** one instance: position, yaw, uniform scale, brightness */
  put(x: number, y: number, z: number, yaw: number, s: number, bright: number): void {
    if (this.n >= this.cap || s <= 0.001) return;
    const n = this.n++;
    _q.setFromAxisAngle(_a.set(0, 1, 0), yaw); _p.set(x, y, z); _s.set(s, s, s);
    this.mesh.setMatrixAt(n, _m.compose(_p, _q, _s));
    const C = this.mesh.instanceColor!.array as Float32Array; C[n * 3] = C[n * 3 + 1] = C[n * 3 + 2] = bright;
    const I = this.inst.array as Float32Array; I[n * 3] = Math.cos(yaw); I[n * 3 + 1] = Math.sin(yaw); I[n * 3 + 2] = s;
  }
  end(): void {
    const n = this.n, b = this.mesh, h = this.hull;
    b.count = n; b.visible = n > 0; if (h) { h.count = n; h.visible = n > 0; }
    if (n) { b.instanceMatrix.needsUpdate = true; b.instanceColor!.needsUpdate = true; this.inst.needsUpdate = true; }
  }
  dispose(scene: THREE.Scene): void { scene.remove(this.mesh); this.mesh.dispose(); if (this.hull) { scene.remove(this.hull); this.hull.dispose(); } }
}

/** render-only readability scale (like pickups.ts READ_SCALE): the gameplay camera is ~25 m away */
const S_CACHE = 1.35, S_CHEST = 1.25, S_BOSS = 1.75, S_ITEM = 2.0;
const ITEM_KINDS: readonly FieldKind[] = ['mend', 'vacuum', 'clear', 'fury'];
/** item tint (ground ring / beacon / pickup VFX) */
const ITEM_COL: Record<FieldKind, [number, number, number, number]> = {
  mend: [0.35, 1, 0.55, 0x5dff8a], vacuum: [0.35, 0.6, 1, 0x4f8cff], clear: [1, 0.85, 0.3, 0xffd24a], fury: [1, 0.35, 0.18, 0xff5a2a],
};
const GHOSTS = 4, BURSTS = 3;

export class FieldLootArt {
  private cacheBase: Bodies; private cacheLid: Bodies; private cacheCore: Bodies;
  private chestBody: Bodies[]; private chestLid: Bodies[];
  private item: Bodies[];
  private marks: AtlasQuads; private beams: AtlasQuads; private glow: AtlasQuads;
  private flat = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));
  private beamQ = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, Math.PI / 4, 0));
  private camQ = new THREE.Quaternion();
  /** chest open sequences: x, z, boss, t0 */
  private ghosts = new Float32Array(GHOSTS * 4); private gn = 0;
  /** Sunburst ground waves: x, z, r, t0 */
  private bursts = new Float32Array(BURSTS * 4); private bn = 0;
  /** render time each cache (by index) finished opening (the lid settles beside it), -1 = not yet */
  private spentAt = new Float32Array(8).fill(-1);
  private emberAcc = 0;
  private lastSim: Sim | null = null;
  private warm = false;
  private t = 0;
  stats = { caches: 0, chests: 0, items: 0, draws: 0 };

  constructor(private scene: THREE.Scene, style: RenderStyle, private groundY: (x: number, z: number) => number, private fx: Fx) {
    this.cacheBase = new Bodies(scene, style, cacheBase(), 8, S_CACHE, 0.035);
    this.cacheLid = new Bodies(scene, style, cacheLid(), 8, S_CACHE, 0.035);
    this.cacheCore = new Bodies(scene, style, cacheCore(), 8, S_CACHE, 0.03);
    this.chestBody = [new Bodies(scene, style, chestBody(ELITE_PAL), 12 + GHOSTS, S_CHEST, 0.035), new Bodies(scene, style, chestBody(BOSS_PAL), 12 + GHOSTS, S_BOSS, 0.035)];
    this.chestLid = [new Bodies(scene, style, chestLid(ELITE_PAL), 12 + GHOSTS, S_CHEST, 0.035), new Bodies(scene, style, chestLid(BOSS_PAL), 12 + GHOSTS, S_BOSS, 0.035)];
    this.item = [mendLook(), vacuumLook(), clearLook(), furyLook()].map((L) => new Bodies(scene, style, L, 6, S_ITEM, 0.03));
    const tex = atlas();
    this.marks = new AtlasQuads(new THREE.PlaneGeometry(1, 1), tex, 48, false, 2);
    const a = new THREE.PlaneGeometry(1, 1); a.translate(0, 0.5, 0); const b = a.clone().rotateY(Math.PI / 2);
    const bg = new THREE.BufferGeometry(); // two crossed vertical cards, base at the origin
    bg.setAttribute('position', new THREE.Float32BufferAttribute([...a.getAttribute('position').array, ...b.getAttribute('position').array], 3));
    bg.setAttribute('uv', new THREE.Float32BufferAttribute([...a.getAttribute('uv').array, ...b.getAttribute('uv').array], 2));
    const ai = a.index!.array as ArrayLike<number>; bg.setIndex([...Array.from(ai), ...Array.from(ai, (v) => v + 4)]);
    this.beams = new AtlasQuads(bg, tex, 40, true, 4);
    this.glow = new AtlasQuads(new THREE.PlaneGeometry(1, 1), tex, 24, true, 5);
    scene.add(this.marks.mesh, this.beams.mesh, this.glow.mesh);
  }

  private get all(): Bodies[] { return [this.cacheBase, this.cacheLid, this.cacheCore, ...this.chestBody, ...this.chestLid, ...this.item]; }
  /** draw one hidden instance of every mesh (under the ground) on the next frame, so every pipeline compiles up front */
  prewarm(): void { this.warm = true; }
  dispose(): void { for (const b of this.all) b.dispose(this.scene); for (const q of [this.marks, this.beams, this.glow]) { this.scene.remove(q.mesh); q.mesh.dispose(); } }

  private mark(x: number, z: number, d: number, cell: number, alpha: number, yaw: number, r = 1, g = 1, b = 1, lift = 0.05): void {
    _q.setFromAxisAngle(_a.set(0, 1, 0), yaw).multiply(this.flat);
    _p.set(x, this.groundY(x, z) + lift, z); _s.set(d, d, 1);
    this.marks.push(_m.compose(_p, _q, _s), cell, alpha, r, g, b);
  }
  private glowMark(x: number, y: number, z: number, d: number, cell: number, alpha: number, yaw: number, r: number, g: number, b: number): void {
    _q.setFromAxisAngle(_a.set(0, 1, 0), yaw).multiply(this.flat);
    _p.set(x, y, z); _s.set(d, d, 1);
    this.glow.push(_m.compose(_p, _q, _s), cell, alpha, r, g, b);
  }
  private beam(x: number, y: number, z: number, h: number, w: number, alpha: number, r: number, g: number, b: number): void {
    _p.set(x, y, z); _s.set(w, h, w);
    this.beams.push(_m.compose(_p, this.beamQ, _s), CELL.beacon, alpha, r, g, b);
  }
  private bill(x: number, y: number, z: number, s: number, roll: number, cell: number, alpha: number, r: number, g: number, b: number): void {
    _q2.setFromAxisAngle(_z, roll); _q.copy(this.camQ).multiply(_q2); _p.set(x, y, z); _s.set(s, s, 1);
    this.glow.push(_m.compose(_p, _q, _s), cell, alpha, r, g, b);
  }
  /** a slow pulse whose rate never exceeds the Reduce flashing cap */
  private pulse(hz: number, ph = 0): number { return Math.sin(this.t * FLASH.rate(hz * TAU) + ph); }

  /** loot SimEvents → one-off VFX (Fx primitives are already FLASH-gated) */
  event(ev: SimEvent, sim: Sim): void {
    const fx = this.fx;
    switch (ev.t) {
      case 'cacheOpen': {
        const gy = this.groundY(ev.x, ev.z);
        fx.particles.emit(ev.x, gy + 1.2, ev.z, 18, 2.2, 0xc8a8ff, 0.32, 1.1, 3.5, -0.5, 1.6, 2.2);
        fx.rings.add(ev.x, gy + 0.12, ev.z, 3.2, 0.7, 0xb890ff, 1.6, 1);
        break;
      }
      case 'cacheGrant': {
        const i = sim.loot.caches.findIndex((c) => c.id === ev.id); if (i >= 0 && i < this.spentAt.length) this.spentAt[i] = this.t;
        const gy = this.groundY(ev.x, ev.z), col = ev.grant === 'pick' ? 0xd8c0ff : ev.grant === 'heal' ? 0x7dffa8 : ev.grant === 'shards' ? 0x7fe8ff : 0xffe08a;
        fx.stars.add(ev.x, gy + 2.2, ev.z, 2.4, 0.4, col, 2.2);
        fx.particles.emit(ev.x, gy + 1.8, ev.z, 26, 4.5, col, 0.34, 1.0, 3, -2, 1.6, 2.4);
        fx.rings.add(ev.x, gy + 0.12, ev.z, 5.5, 0.9, col, 1.8, 1.5);
        fx.lights.flash(ev.x, gy + 1.5, ev.z, col, 22, 0.5, 14);
        break;
      }
      case 'chestDrop': {
        const gy = this.groundY(ev.x, ev.z), col = ev.boss ? 0xffd860 : 0x7fe8f0;
        fx.rings.add(ev.x, gy + 0.12, ev.z, ev.boss ? 4.5 : 2.6, 0.6, col, 1.4, 1);
        fx.particles.emit(ev.x, gy + 0.6, ev.z, ev.boss ? 16 : 8, 2.5, col, 0.25, 0.8, 2, -2, 1.6, 2);
        break;
      }
      case 'chestOpen': {
        if (this.gn >= GHOSTS) { this.ghosts.copyWithin(0, 4); this.gn--; }
        const o = this.gn++ * 4; this.ghosts[o] = ev.x; this.ghosts[o + 1] = ev.z; this.ghosts[o + 2] = ev.boss ? 1 : 0; this.ghosts[o + 3] = this.t;
        const gy = this.groundY(ev.x, ev.z), col = ev.boss ? 0xffd860 : 0x9ff8ee, k = ev.boss ? 1.4 : 1;
        fx.stars.add(ev.x, gy + 1.2 * k, ev.z, 2.6 * k, 0.35, col, 2.4);                       // gated by FLASH (big star budget)
        fx.lights.flash(ev.x, gy + 1.2, ev.z, col, ev.boss ? 34 : 24, 0.6, 16);                  // gated by FLASH (big light budget)
        fx.particles.emit(ev.x, gy + 0.9 * k, ev.z, Math.round(22 + 6 * ev.n), 3.2, col, 0.3, 1.3, 5.5, -1.2, 1.4, 2.4);
        fx.rings.add(ev.x, gy + 0.12, ev.z, 4 * k, 0.8, col, 1.8, 1.4);
        break;
      }
      case 'fieldPick': {
        const gy = this.groundY(ev.x, ev.z), c = ITEM_COL[ev.kind], P = sim.player, hy = this.groundY(P.x, P.z);
        fx.particles.emit(ev.x, gy + 1, ev.z, 14, 3, c[3], 0.28, 0.8, 2.5, -1, 1.6, 2.2);
        fx.rings.add(ev.x, gy + 0.12, ev.z, 2.4, 0.5, c[3], 1.5, 1);
        if (ev.kind === 'mend') { fx.particles.emit(P.x, hy + 1.2, P.z, 22, 1.4, 0x7dffa8, 0.3, 1.1, 3.5, -0.3, 1.2, 2); fx.rings.add(P.x, hy + 0.15, P.z, 2.2, 0.6, 0x7dffa8, 1.2, 1); }
        else if (ev.kind === 'vacuum') { fx.rings.add(P.x, hy + 0.2, P.z, 36, 1.2, 0x4f8cff, 1.8, 1.5); fx.rings.add(P.x, hy + 0.2, P.z, 14, 0.8, 0x9cc0ff, 1.4, 1); }
        else if (ev.kind === 'fury') { fx.rings.add(P.x, hy + 0.15, P.z, 3.4, 0.6, 0xff5a2a, 1.8, 1.2); fx.particles.emit(P.x, hy + 1, P.z, 24, 3, 0xff7a3a, 0.3, 0.9, 3, -1, 1.4, 2.4); }
        break;
      }
      case 'fieldExpire': { const gy = this.groundY(ev.x, ev.z); fx.particles.emit(ev.x, gy + 0.9, ev.z, 8, 1.2, 0xb8c0c8, 0.22, 0.6, 1, 0, 2, 1.2); break; }
      case 'clearBurst': {
        if (this.bn >= BURSTS) { this.bursts.copyWithin(0, 4); this.bn--; }
        const o = this.bn++ * 4; this.bursts[o] = ev.x; this.bursts[o + 1] = ev.z; this.bursts[o + 2] = ev.r; this.bursts[o + 3] = this.t;
        const gy = this.groundY(ev.x, ev.z);
        fx.rings.add(ev.x, gy + 0.2, ev.z, ev.r, 0.75, 0xffd24a, 2.2, 2);
        fx.rings.add(ev.x, gy + 0.25, ev.z, ev.r * 0.6, 0.5, 0xfff2b0, 1.6, 1.4);
        fx.stars.add(ev.x, gy + 1.6, ev.z, 3.2, 0.3, 0xffe08a, 2.2);         // one big star, counted against the flash budget
        fx.lights.flash(ev.x, gy + 2, ev.z, 0xffd24a, 40, 0.5, 30);         // counted against the flash budget
        fx.shake = Math.min(1, fx.shake + 0.25);
        break;
      }
    }
  }

  sync(sim: Sim, t: number, dt: number, cam: THREE.Camera): void {
    if (sim !== this.lastSim) { this.lastSim = sim; this.gn = 0; this.bn = 0; this.spentAt.fill(-1); }
    this.t = t; this.camQ.copy(cam.quaternion);
    for (const b of this.all) b.n = 0;
    this.marks.n = 0; this.beams.n = 0; this.glow.n = 0;
    const L = sim.loot, P = sim.player;
    if (this.warm) { this.warm = false; for (const b of this.all) b.put(P.x, -60, P.z, 0, 1, 1); }
    // ---- relic caches
    let nc = 0;
    for (let i = 0; i < L.caches.length; i++) {
      const c = L.caches[i], gy = this.groundY(c.x, c.z), d = Math.hypot(c.x - P.x, c.z - P.z), yaw = i * 1.7;
      const top = gy + 0.78 * S_CACHE;
      if (c.state === 0) {
        nc++;
        const glow = 1 + 0.08 * this.pulse(0.6, i);
        this.cacheBase.put(c.x, gy, c.z, yaw, 1, glow);
        this.cacheLid.put(c.x, top, c.z, yaw, 1, glow);
        this.mark(c.x, c.z, 3.4, CELL.rune, 0.85, t * 0.15 + i);
        // locator column: tall and faint far away (findable across the big map), fading out as you get close
        const far = Math.min(1, Math.max(0, (d - 8) / 20));
        this.beam(c.x, gy + 0.3, c.z, 18, 0.9, 0.14 + 0.3 * far, 0.75, 0.6, 1);
        this.beam(c.x, gy + 0.3, c.z, 3.2, 0.5, 0.35, 1, 0.85, 0.45);
      } else if (c.state === 1) {
        nc++;
        const u = Math.min(1, c.t / CACHE_REVEAL), e = ease(u);
        this.cacheBase.put(c.x, gy, c.z, yaw, 1, 1 + 0.4 * u);
        this.cacheLid.put(c.x, top + 1.1 * e, c.z, yaw + e * 2.4, 1 + 0.1 * e, 1.15);
        this.cacheCore.put(c.x, top + 0.2 + 1.0 * e, c.z, t * 2.2, Math.max(0.01, e), 1.3 + 0.4 * u);
        this.beam(c.x, gy + 0.3, c.z, 6 + 6 * e, 1.0 + 1.6 * e, 0.4 + 0.5 * u * FLASH.dim, 0.85, 0.7, 1);
        this.mark(c.x, c.z, 3.4 + 2 * e, CELL.rune, 0.9, t * 0.15 + i + e * 2);
        this.mark(c.x, c.z, 4 + 3 * e, CELL.inkRing, 0.55 * (1 - 0.5 * u), 0, 0.62, 0.38, 1, 0.07);
      } else {
        // spent: the lid settles beside the dimmed casket (0.4 s after the reward lands)
        const s0 = this.spentAt[i] ?? -1, a = s0 >= 0 ? Math.min(1, (t - s0) / 0.4) : 1;
        const lx = c.x + Math.cos(yaw) * 0.85 * S_CACHE, lz = c.z + Math.sin(yaw) * 0.85 * S_CACHE, ly = this.groundY(lx, lz);
        this.cacheBase.put(c.x, gy, c.z, yaw, 1, 0.55 + 0.45 * (1 - a));
        this.cacheLid.put(c.x + (lx - c.x) * a, (top + 1.1) * (1 - a) + ly * a, c.z + (lz - c.z) * a, yaw + 2.4 + a * 0.6, 1, 0.55 + 0.6 * (1 - a));
        if (a < 1) { this.cacheCore.put(c.x, top + 1.2 + a, c.z, t * 2.2, Math.max(0.01, 1 - a), 1.6); this.beam(c.x, gy + 0.3, c.z, 12, 2.6 * (1 - a), 0.9 * (1 - a) * FLASH.dim, 0.85, 0.7, 1); }
        this.mark(c.x, c.z, 3.4, CELL.rune, 0.25, i);
      }
    }
    // ---- chests (sitting) and chest open sequences
    let nch = 0;
    for (const c of L.chests) {
      if (!c.alive) continue;
      nch++;
      const k = c.boss ? 1 : 0, S = c.boss ? S_BOSS : S_CHEST, gy = this.groundY(c.x, c.z), yaw = (c.id * 2.399) % TAU;
      // falls in with a small bounce over the first 0.45 s
      const u = Math.min(1, c.age / 0.45), drop = u < 0.6 ? (1 - u / 0.6) ** 2 * 2.2 : Math.sin(((u - 0.6) / 0.4) * Math.PI) * 0.18;
      const glow = 1 + 0.1 * this.pulse(0.7, c.id);
      this.chestBody[k].put(c.x, gy + drop, c.z, yaw, 1, glow);
      this.chestLid[k].put(c.x, gy + drop + 0.56 * S, c.z, yaw, 1, glow);
      const col = c.boss ? [1, 0.85, 0.4] : [0.55, 1, 0.95];
      this.mark(c.x, c.z, (c.boss ? 3.6 : 2.6), CELL.chest, 0.9, yaw + t * 0.25, col[0], col[1], col[2]);
      this.beam(c.x, gy + 0.3, c.z, c.boss ? 9 : 5, c.boss ? 0.8 : 0.5, 0.32, col[0], col[1], col[2]);
    }
    for (let g = 0; g < this.gn; g++) {
      const o = g * 4, a = t - this.ghosts[o + 3];
      if (a > 1.1 || a < 0) { this.gn--; if (g !== this.gn) this.ghosts.copyWithin(o, this.gn * 4, this.gn * 4 + 4); g--; continue; }
      const x = this.ghosts[o], z = this.ghosts[o + 1], k = this.ghosts[o + 2] | 0, S = k ? S_BOSS : S_CHEST, gy = this.groundY(x, z), yaw = 0.6;
      const sink = a > 0.75 ? (a - 0.75) / 0.35 : 0;
      this.chestBody[k].put(x, gy - sink * 0.4, z, yaw, Math.max(0.01, 1 - sink), 1.25 - 0.4 * sink);
      const lu = Math.min(1, a / 0.7), le = ease(lu);
      this.chestLid[k].put(x, gy + 0.56 * S + 2.4 * le, z, yaw + le * 3.4, Math.max(0.01, 1 - Math.max(0, lu - 0.6) * 2.2), 1.3);
      const fade = 1 - Math.min(1, a / 1.0), col = k ? [1, 0.85, 0.4] : [0.6, 1, 0.95];
      this.beam(x, gy + 0.2, z, 8 * S, (0.9 + 0.6 * ease(Math.min(1, a / 0.25))) * S, 0.85 * fade * FLASH.dim, col[0], col[1], col[2]);
      this.glowMark(x, gy + 0.1, z, (2.4 + 3 * ease(Math.min(1, a / 0.6))) * S, CELL.ring, 0.7 * fade * FLASH.dim, 0, col[0], col[1], col[2]);
    }
    // ---- field items (bob + turn; blink near the end)
    let ni = 0;
    const blinkHz = Math.min(4, FLASH.L.maxHz), soft = FLASH.mode !== 'off';
    for (const it of L.items) {
      if (!it.alive) continue;
      ni++;
      const ki = ITEM_KINDS.indexOf(it.kind), c = ITEM_COL[it.kind], gy = this.groundY(it.x, it.z), age = FIELD_LIFE - it.life, ph = it.id * 1.3;
      let s = Math.min(1, age / 0.25), bright = 1 + 0.08 * this.pulse(1.1, ph);
      if (it.life < FIELD_BLINK) {
        const hz = it.life < 1.5 ? blinkHz : blinkHz * 0.6, on = Math.sin(t * hz * TAU) > -0.2;
        if (!on) { if (!soft) continue; s *= 0.78; bright *= 0.45; } // Reduce flashing: a soft dim instead of a hard on/off
      }
      const y = gy + 0.95 + Math.sin(t * 1.6 + ph) * 0.08;
      this.item[ki].put(it.x, y, it.z, t * 0.9 + ph, s, bright);
      this.mark(it.x, it.z, 2.1, CELL.inkRing, 0.85 * s, 0, c[0], c[1], c[2]);
      this.beam(it.x, gy + 0.3, it.z, 2.4, 0.4, 0.4 * s, c[0], c[1], c[2]);
      if (it.kind === 'clear') this.bill(it.x, y, it.z, 1.6 * s, t * 0.4, CELL.rays, 0.35 * FLASH.dim, 1, 0.85, 0.4);
    }
    // ---- Sunburst ground wave (expands to the burst radius, fades)
    for (let k = 0; k < this.bn; k++) {
      const o = k * 4, a = t - this.bursts[o + 3];
      if (a > 0.7 || a < 0) { this.bn--; if (k !== this.bn) this.bursts.copyWithin(o, this.bn * 4, this.bn * 4 + 4); k--; continue; }
      const u = a / 0.7, x = this.bursts[o], z = this.bursts[o + 1], r = this.bursts[o + 2] * ease(Math.min(1, a / 0.45));
      this.glowMark(x, this.groundY(x, z) + 0.15, z, r * 2.1, CELL.rays, 0.55 * (1 - u) * (1 - u) * FLASH.dim, u * 0.5, 1, 0.78, 0.3);
    }
    // ---- Fervor: a slow crimson ring under the hero and a few rising embers while it lasts
    if (L.furyT > 0) {
      const hy = this.groundY(P.x, P.z), fade = Math.min(1, L.furyT / 1.5);
      this.mark(P.x, P.z, 3.4 + 0.25 * this.pulse(0.8), CELL.inkRing, (0.75 + 0.12 * this.pulse(0.8)) * fade, 0, 1, 0.22, 0.1, 0.07);
      this.emberAcc += dt;
      while (this.emberAcc > 0.07) {
        this.emberAcc -= 0.07;
        const a = Math.random() * TAU, d = 0.4 + Math.random() * 0.6;
        this.fx.particles.emitDir(P.x + Math.cos(a) * d, hy + 0.4 + Math.random() * 1.2, P.z + Math.sin(a) * d, Math.cos(a) * 0.3, 1.6 + Math.random(), Math.sin(a) * 0.3, Math.random() < 0.5 ? 0xff5a2a : 0xffa040, 0.16, 0.7, 2);
      }
    } else this.emberAcc = 0;
    // flush
    let draws = 0;
    for (const b of this.all) { b.end(); if (b.n) draws += b.hull ? 2 : 1; }
    this.marks.end(); this.beams.end(); this.glow.end();
    draws += (this.marks.n ? 1 : 0) + (this.beams.n ? 1 : 0) + (this.glow.n ? 1 : 0);
    this.stats.caches = nc; this.stats.chests = nch; this.stats.items = ni; this.stats.draws = draws;
  }
}
